import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface DocumentTypeOption {
  id: string;
  name: string;
}

export interface UseDocumentTypeOptions {
  options: DocumentTypeOption[];
  createType: (name: string) => Promise<DocumentTypeOption | null>;
  reload: () => Promise<void>;
}

/**
 * 'canvas' is a Projects-owned built-in document type. It is hidden from the
 * Type picker, but a document already typed `canvas` must still display its
 * name, so the document's CURRENT type is merged into the option list even when
 * it is hidden or archived.
 */
const HIDDEN_TYPE_KEYS = new Set(['canvas']);

/**
 * Org-scoped document Type options for the editor/table pickers. Loads active
 * types (excluding hidden ones), ordered default-first then alphabetical, and
 * guarantees the document's current type is present (by id) even if hidden or
 * archived. `createType` mints a new org-saved type via the create_document_type
 * RPC and folds it into the local list.
 */
export function useDocumentTypeOptions(
  orgId: string | undefined,
  currentTypeId: string | undefined,
  onError?: (message: string) => void
): UseDocumentTypeOptions {
  const [options, setOptions] = useState<DocumentTypeOption[]>([]);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const load = useCallback(async () => {
    if (!orgId) {
      setOptions([]);
      return;
    }
    const { data, error } = await supabase
      .from('document_types')
      .select('id, name, key')
      .eq('org_id', orgId)
      .is('archived_at', null)
      .order('is_default', { ascending: false })
      .order('name', { ascending: true });
    if (error) {
      onErrorRef.current?.(error.message);
      return;
    }
    let opts: DocumentTypeOption[] = (data ?? [])
      .filter((t) => !HIDDEN_TYPE_KEYS.has(t.key as string))
      .map((t) => ({ id: t.id as string, name: t.name as string }));
    // Ensure the document's current type is selectable/displayable even if it
    // is hidden (canvas) or archived.
    if (currentTypeId && !opts.some((o) => o.id === currentTypeId)) {
      const { data: cur } = await supabase
        .from('document_types')
        .select('id, name')
        .eq('id', currentTypeId)
        .maybeSingle();
      if (cur) opts = [...opts, { id: cur.id as string, name: cur.name as string }];
    }
    setOptions(opts);
  }, [orgId, currentTypeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const createType = useCallback(
    async (name: string): Promise<DocumentTypeOption | null> => {
      if (!orgId) return null;
      const { data, error } = await supabase.rpc('create_document_type', {
        p_org_id: orgId,
        p_name: name
      });
      if (error) {
        onErrorRef.current?.(error.message);
        return null;
      }
      const env = data as
        | { ok?: boolean; error?: string; type?: { id: string; name: string } }
        | null;
      if (!env || env.ok !== true || !env.type) {
        onErrorRef.current?.(env?.error ?? 'Could not create type');
        return null;
      }
      const opt: DocumentTypeOption = { id: env.type.id, name: env.type.name };
      setOptions((prev) =>
        prev.some((o) => o.id === opt.id) ? prev : [...prev, opt]
      );
      return opt;
    },
    [orgId]
  );

  return { options, createType, reload: load };
}
